import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Camera, Video, ChevronRight, MapPin, Clock, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import { authFetch } from '../lib/authFetch';
import useTheme4A from '../components/useTheme4A';
import useAllowPrint from '../components/useAllowPrint';

const FELLOWSHIP_OPTIONS = [
  { value: 'AA', label: 'AA  ·  Alcoholics Anonymous' },
  { value: 'NA', label: 'NA  ·  Narcotics Anonymous' },
  { value: 'ACA', label: 'ACA  ·  Adult Children of Alcoholics' },
  { value: 'SMART', label: 'SMART Recovery' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Capture a single GPS point with the browser geolocation API.
 * Returns { lat, lng, accuracy_m, captured_at } or null if denied / unavailable.
 */
function captureGps() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    const timeoutId = setTimeout(() => resolve(null), 8000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timeoutId);
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          captured_at: new Date().toISOString(),
        });
      },
      () => { clearTimeout(timeoutId); resolve(null); },
      { enableHighAccuracy: true, timeout: 7000, maximumAge: 0 }
    );
  });
}

function fmtDuration(mins) {
  if (!mins && mins !== 0) return '--';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) +
      '  ·  ' +
      d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(iso);
  }
}

function MeetingLogPage() {
  useAllowPrint();
  useTheme4A();
  const navigate = useNavigate();
  const [meetingType, setMeetingType] = useState('AA');
  const [activeMeeting, setActiveMeeting] = useState(null);   // server's in-progress meeting, if any
  const [showCamera, setShowCamera] = useState(null);          // 'before' | 'after' | null
  const [meetings, setMeetings] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    fetchActive();
    fetchMeetings();
  }, []);

  const fetchActive = async () => {
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/active`);
      if (r.ok) {
        const data = await r.json();
        setActiveMeeting(data.meeting || null);
      }
    } catch (e) { /* ignore */ }
  };

  const fetchMeetings = async () => {
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings`);
      if (r.ok) {
        const data = await r.json();
        setMeetings(data);
      }
    } catch (e) { /* ignore */ }
  };

  const startCamera = async (kind) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });
      streamRef.current = stream;
      setShowCamera(kind);
      // Wait one tick for the <video> to mount, then attach.
      requestAnimationFrame(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      });
    } catch (e) {
      toast.error('Camera access denied.  Allow camera access to take a selfie or photo of the venue.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setShowCamera(null);
  };

  const captureAndSubmit = async () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

    const kind = showCamera;
    stopCamera();
    setSubmitting(true);
    toast.message('Capturing your location...', { duration: 1500 });
    const gps = await captureGps();
    if (!gps) {
      toast.warning('GPS not available.  Selfie saved without location verification.');
    }

    try {
      if (kind === 'before') {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/check-in`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            meeting_type: meetingType,
            meeting_format: 'in-person',
            before_selfie: dataUrl,
            before_gps: gps,
          }),
        });
        const data = await r.json();
        if (!r.ok || !data.ok) {
          toast.error(data.message || 'Could not start the meeting.');
        } else {
          setActiveMeeting(data.meeting);
          toast.success('Meeting started.  Camera, microphone and audio are now locked until you take your After selfie.');
        }
      } else if (kind === 'after' && activeMeeting) {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/check-out`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            meeting_id: activeMeeting.meeting_id,
            after_selfie: dataUrl,
            after_gps: gps,
          }),
        });
        const data = await r.json();
        if (!r.ok || !data.ok) {
          toast.error('Could not end the meeting.  Try again.');
        } else {
          setActiveMeeting(null);
          toast.success('Meeting completed and added to your verified history.');
          fetchMeetings();
        }
      }
    } catch (e) {
      toast.error('Network error.  Please try again.');
    }
    setSubmitting(false);
  };

  // Camera viewfinder modal
  if (showCamera) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="camera-modal">
        <div className="flex-1 relative flex items-center justify-center">
          <video ref={videoRef} autoPlay playsInline className="max-w-full max-h-full" />
        </div>
        <div className="bg-black p-6 flex items-center justify-around">
          <Button variant="ghost" onClick={stopCamera} className="text-white" data-testid="camera-cancel-btn">
            Cancel
          </Button>
          <button
            onClick={captureAndSubmit}
            disabled={submitting}
            className="w-16 h-16 rounded-full bg-white border-4 border-zinc-300 disabled:opacity-50"
            data-testid="camera-capture-btn"
            aria-label="Capture"
          />
          <span className="text-zinc-300 text-xs uppercase tracking-wider">{showCamera === 'before' ? 'Before' : 'After'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      <PageHeader title="Meeting Log" />
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">

        {/* Clarifying statement explaining the flow + why */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-[12.5px] text-blue-900 leading-relaxed" data-testid="meeting-log-explainer">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-blue-700" />
            <span className="font-bold uppercase tracking-wider text-[10px] text-blue-800">How meeting verification works</span>
          </div>
          <p className="mb-2">
            <strong>Take a selfie before the meeting starts.</strong>  This can be a photo of yourself or a photo of the venue you are about to enter.  When you tap "Take selfie before meeting" the app captures your GPS location at that moment.
          </p>
          <p className="mb-2">
            <strong>Stay in the meeting.</strong>  The app stays open in the background but the camera, microphone and speakers are <strong>locked</strong> across the app until your After selfie is taken.  This keeps you fully present in the room.
          </p>
          <p className="mb-2">
            <strong>Take a second selfie within 30 minutes after the meeting ends.</strong>  This pairs with your Before selfie and your GPS to confirm attendance.  Most AA / NA / ACA meetings run 60 to 90 minutes, so allow time afterwards for fellowship before taking your After selfie.
          </p>
          <p>
            If 2.5 hours pass without an After selfie, Tudor will check in with you.  Your verified attendance is then added to <strong>Recent Meetings</strong> below.
          </p>
        </div>

        {/* In-Progress Banner (camera/mic/audio locked) */}
        {activeMeeting && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-4" data-testid="meeting-in-progress-banner">
            <div className="flex items-center gap-2 mb-1">
              <Loader2 className="w-4 h-4 text-amber-700 animate-spin" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Meeting in progress</span>
            </div>
            <p className="text-sm font-semibold text-amber-900">
              You're checked in at {/^[aeiouAEIOU]/.test(activeMeeting.meeting_type || '') ? 'an' : 'a'} {activeMeeting.meeting_type} meeting.
            </p>
            <p className="text-[12px] text-amber-800 mt-1 leading-relaxed">
              Started {fmtDate(activeMeeting.started_at)}.  The camera, microphone and audio are locked across the app.  Take your After selfie to release them.
            </p>
          </div>
        )}

        {/* Selfie-flow controls */}
        <div className="bg-white rounded-xl p-4 border border-zinc-200">
          {/* Fellowship selector: only used before check-in */}
          {!activeMeeting && (
            <div className="mb-4">
              <Label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Meeting fellowship</Label>
              <Select value={meetingType} onValueChange={setMeetingType}>
                <SelectTrigger className="mt-2" data-testid="meeting-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FELLOWSHIP_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`meeting-type-option-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Before */}
          <div className="mb-3">
            <Button
              onClick={() => startCamera('before')}
              disabled={!!activeMeeting || submitting}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="take-before-selfie-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Take selfie before meeting
            </Button>
            <p className="text-[10px] text-zinc-500 mt-1.5 leading-relaxed px-1">
              Yourself or a photo of the venue.  Captures GPS automatically.
            </p>
          </div>

          {/* After */}
          <div>
            <Button
              onClick={() => startCamera('after')}
              disabled={!activeMeeting || submitting}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="take-after-selfie-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Take selfie after meeting
            </Button>
            <p className="text-[10px] text-zinc-500 mt-1.5 leading-relaxed px-1">
              Take within 30 minutes after the meeting ends to verify attendance.
            </p>
          </div>
        </div>

        {/* Zoom shortcut */}
        <button
          onClick={() => navigate('/meetings/zoom')}
          className="w-full bg-white rounded-xl border border-zinc-200 p-4 text-left flex items-center gap-3 hover:bg-zinc-50"
          data-testid="zoom-shortcut-btn"
        >
          <Video className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-zinc-900">Log a Zoom meeting</p>
            <p className="text-[11px] text-zinc-500">Use the unique attendance link from your scheduled Zoom meeting.</p>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-400" />
        </button>

        {/* Recent Meetings: verified attendance history built from selfie pairs */}
        <div className="bg-white rounded-xl p-4 border border-zinc-200" data-testid="recent-meetings-section">
          <h2 className="text-sm font-bold text-zinc-900 mb-3">Recent Meetings</h2>
          {meetings.length === 0 ? (
            <p className="text-sm text-zinc-500">No verified meetings yet.  Your first meeting will appear here once your After selfie is taken.</p>
          ) : (
            <ul className="space-y-2" data-testid="recent-meetings-list">
              {meetings.slice(0, 20).map((m) => {
                const verified = !!(m.verified || (m.before_selfie && m.after_selfie && !m.auto_closed));
                const notVerified = m.auto_closed || (!verified && m.status === 'completed');
                return (
                  <li key={m.meeting_id} className="border border-zinc-100 rounded-lg p-3 flex items-start gap-3" data-testid={`recent-meeting-${m.meeting_id}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${verified ? 'bg-emerald-100' : 'bg-zinc-100'}`}>
                      <ShieldCheck className={`w-4 h-4 ${verified ? 'text-emerald-700' : 'text-zinc-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-zinc-900">{m.meeting_type}</span>
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
                          {(m.meeting_format || 'in-person') === 'zoom' ? 'Zoom' : 'In-person'}
                        </span>
                        {verified && <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Verified</span>}
                        {notVerified && !verified && <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold" data-testid={`not-verified-${m.meeting_id}`}>Not verified</span>}
                      </div>
                      <p className="text-[11px] text-zinc-500 mt-0.5">{fmtDate(m.meeting_date || m.created_at)}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-zinc-500">
                        {m.duration_minutes != null && (
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDuration(m.duration_minutes)}</span>
                        )}
                        {m.location_label && (
                          <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /> {m.location_label}</span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default MeetingLogPage;
